/**
 * Продуктовые события → «Продуктовые метрики» (тип 3, спека 19 §2.9 бэка).
 *
 * События копятся в памяти по воркспейсам и уходят пачкой раз в минуту, а при
 * скрытии вкладки и уходе со страницы — с `keepalive`, чтобы запрос не потерялся.
 * Best-effort: ошибки не показываем и пачку не повторяем (это счётчики, не данные).
 */
import {useCallback, useEffect, useRef} from 'react';
import {useActiveWorkspace} from '@api/workspaces';
import {IS_MOCK} from '../mock-mode';
import {userMetricsClient} from './client';
import {browserTimezone} from './heartbeat';
import type {ProductEventName} from './types';

const FLUSH_INTERVAL_MS = 60_000;

/** Воркспейс → событие → сколько раз с последней отправки. */
const pending = new Map<string, Map<ProductEventName, number>>();

/** Учесть событие в воркспейсе (без воркспейса — некуда записать, пропускаем). */
export const trackProductEvent = (
  workspaceSid: string | null | undefined,
  event: ProductEventName,
): void => {
  if (IS_MOCK || !workspaceSid) return;
  const counts =
    pending.get(workspaceSid) ?? new Map<ProductEventName, number>();
  counts.set(event, (counts.get(event) ?? 0) + 1);
  pending.set(workspaceSid, counts);
};

/** Отправить накопленное (по запросу на воркспейс). */
export const flushProductEvents = (keepalive = false): void => {
  if (pending.size === 0) return;
  const batches = [...pending.entries()];
  pending.clear();
  const timezone = browserTimezone();
  for (const [workspaceSid, counts] of batches) {
    void userMetricsClient
      .activityEvents(
        {workspaceSid, timezone, counts: Object.fromEntries(counts)},
        {keepalive},
      )
      .catch(() => undefined);
  }
};

/** Отправка по таймеру и при уходе со вкладки — монтируется один раз в AppLayout. */
export const useProductEventsFlush = () => {
  useEffect(() => {
    if (IS_MOCK) return;
    const timer = window.setInterval(
      () => flushProductEvents(),
      FLUSH_INTERVAL_MS,
    );
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushProductEvents(true);
    };
    const onPageHide = () => flushProductEvents(true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      flushProductEvents(true);
    };
  }, []);
};

/** Функция учёта события в активном воркспейсе. */
export const useProductEvent = () => {
  const {activeId} = useActiveWorkspace();
  return useCallback(
    (event: ProductEventName) => trackProductEvent(activeId, event),
    [activeId],
  );
};

/**
 * Открытие страницы — одно событие на её монтирование: дожидаемся активного
 * воркспейса, повторный прогон эффектов (StrictMode) не удваивает счёт.
 */
export const useTrackPageView = (event: ProductEventName) => {
  const {activeId} = useActiveWorkspace();
  const tracked = useRef(false);
  useEffect(() => {
    if (tracked.current || !activeId) return;
    tracked.current = true;
    trackProductEvent(activeId, event);
  }, [activeId, event]);
};
